import React, { useCallback, useEffect, useState } from 'react'
import * as Header from 'views/ui/Header/Header'
import { AntDHeader } from 'views/ui/Header/Header'
import { useAction, useHelmet } from '@agro-club/frontend-shared'
import { useTranslation } from 'react-i18next'
import { Button, Drawer, Flex, Input } from 'antd'
import CSS from 'csstype'
import { useSelector } from 'react-redux'
import SeedTrackerActions from 'modules/domain/seedTracker/duck'
import SeedTrackerSelectors from 'modules/domain/seedTracker/selectors'
import useDownload from 'hooks/useDownload'
import { endpoints } from 'modules/endpoints'
import { useSeedCropList } from 'modules/domain/seedCrop/hooks'
import { useSeedClassList } from 'modules/domain/seedClass/hooks'
import { getSeedInventoryGrowerCompanies } from 'modules/domain/company/managers'
import { SeedCrop } from 'modules/domain/seedCrop/types'
import { SeedClass } from 'modules/domain/seedClass/types'
import { SeedBrand } from 'modules/domain/seedTracker/types'
import { Progress } from 'modules/types'
import { Product, ProductListRequestFilter, ProductListRequestSorting } from 'modules/domain/product/types'
import { makeCancelable } from 'modules/utils/httpClient'
import * as productManagers from 'modules/domain/product/managers'
import useLangPicker from 'hooks/useLangPicker'
import GenericSelect from 'views/components/Generics/GenericSelect'
import SeedTrackerList from './SeedTrackerList/SeedTrackerList'
import { drawerFilterStackStyle, filterActionBarStyles, scrollAreaStyle, searchMaxWidthStyle } from './styles'

const seedTrackerContentStyles: CSS.Properties = {
  flex: 1,
  backgroundColor: '#FAFAFA',
}

const seedTrackerHeaderStyles: CSS.Properties = {
  paddingLeft: '1em',
  paddingRight: '1em',
}

const tabContentWrapperStyles: CSS.Properties = {
  padding: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
}

const growerFilterActiveTextStyles: CSS.Properties = {
  fontSize: '16px',
  lineHeight: '16px',
  color: '#52C41A',
  paddingTop: '18px',
}

const growerFilterWidthStyles: CSS.Properties = {
  width: '400px',
  maxWidth: '100%',
}

// Hook to fetch products for the Variety filter
const useProductListForFilter = () => {
  const [progress, setProgress] = useState<Progress>(Progress.IDLE)
  const [list, setList] = useState<Product[]>([])

  useEffect(() => {
    setProgress(Progress.WORK)
    const cancelableFn = makeCancelable(productManagers.getStorefrontProductList)
    const filter: ProductListRequestFilter = {}
    const sorting: ProductListRequestSorting = {}
    const [promise, cancel] = cancelableFn(filter, sorting, 1, 1000)

    promise
      .then((response) => {
        setList(response.data || [])
        setProgress(Progress.SUCCESS)
      })
      .catch((err: any) => {
        if (!err?.isCanceled) {
          setProgress(Progress.ERROR)
        }
      })

    return () => {
      cancel()
    }
  }, [])

  return [progress, list] as const
}

const SeedTrackerTable: React.FC = () => {
  const filter = useSelector(SeedTrackerSelectors.filter)
  const filterUpdated = useAction(SeedTrackerActions.filterUpdated)
  const listRequested = useAction(SeedTrackerActions.listRequested)
  const { t } = useTranslation(['seedTracker', 'labels'])
  const { pick } = useLangPicker()
  const { Search } = Input
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)
  const [growerOptions, setGrowerOptions] = useState<{ label: string; value: string }[]>([])
  const sorting = useSelector(SeedTrackerSelectors.sorting)
  const [progressCSV, downloadCSV] = useDownload(endpoints.seedTrackers('download/csv'), {
    ...filter,
    ...sorting,
  })

  // Fetch Product, Seed Crop and Seed Class lists
  const [, products] = useProductListForFilter()
  const [, crops] = useSeedCropList()
  const [, classes] = useSeedClassList()

  useEffect(() => {
    let cancelled = false
    getSeedInventoryGrowerCompanies()
      .then((response) => {
        if (!cancelled) {
          setGrowerOptions(
            (response.data ?? []).map((company) => ({ value: company.id, label: company.official_name })),
          )
        }
      })
      .catch(() => {
        if (!cancelled) setGrowerOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSearchChange = useCallback(
    (search?: string) => {
      const newFilter = { ...filter, ...{ search } }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const handleSeedCropChange = useCallback(
    (value: SeedCrop | null) => {
      const newFilter = { ...filter, seed_crop_id: value?.id || undefined }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const handleProductChange = useCallback(
    (value: Product | null) => {
      const newFilter = { ...filter, product_id: value?.id || undefined }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const handleSeedClassChange = useCallback(
    (value: SeedClass | null) => {
      const newFilter = { ...filter, seed_class_id: value?.id || undefined }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const handleSeedBrandChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const seedBrand = Array.isArray(value) ? value[0] : value
      const newFilter = {
        ...filter,
        seed_brand: Object.values(SeedBrand).find((brand) => brand === seedBrand),
      }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const handleGrowerChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const companyId = Array.isArray(value) ? value[0] : value
      const newFilter = { ...filter, company_id: companyId ? String(companyId) : undefined }
      filterUpdated(newFilter)
      listRequested({ filter: newFilter, page: 1 })
    },
    [filterUpdated, filter, listRequested],
  )

  const productOptions = products?.map((product) => ({ label: pick(product.title_i18n), value: product.id })) || []
  const cropOptions = crops?.map((crop) => ({ label: crop.name, value: crop.id })) || []
  const classOptions = classes?.map((cls) => ({ label: cls.name, value: cls.id })) || []
  const brandOptions = Object.values(SeedBrand)

  const handleProductSelectChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const productId = Array.isArray(value) ? value[0] : value
      const product = productId ? products?.find((p) => p.id === String(productId)) || null : null
      handleProductChange(product)
    },
    [products, handleProductChange],
  )

  const handleCropSelectChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const cropId = Array.isArray(value) ? value[0] : value
      const crop = cropId ? crops?.find((c) => c.id === String(cropId)) || null : null
      handleSeedCropChange(crop)
    },
    [crops, handleSeedCropChange],
  )

  const handleClassSelectChange = useCallback(
    (value: string | string[] | number | number[]) => {
      const classId = Array.isArray(value) ? value[0] : value
      const seedClass = classId ? classes?.find((c) => c.id === String(classId)) || null : null
      handleSeedClassChange(seedClass)
    },
    [classes, handleSeedClassChange],
  )

  const onSearch = (value: string) => {
    handleSearchChange(value)
  }

  const drawerFilterSelectProps = {
    allowClear: true as const,
    showSearch: true as const,
    parentContainerFlex: 1 as const,
  }

  const filterOption: NonNullable<React.ComponentProps<typeof GenericSelect>['filterOption']> = (input, option) => {
    const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '')
    return label.toLowerCase().includes(input.toLowerCase())
  }

  const brandFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.filters.brandLabel'),
    placeholder: t('list.filters.brandPlaceholder'),
    options: brandOptions.map((brand) => ({ label: brand, value: brand })),
    value: filter.seed_brand || undefined,
    onChange: handleSeedBrandChange,
    ...drawerFilterSelectProps,
  }

  const varietyFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.filters.varietyLabel'),
    placeholder: t('list.filters.varietyPlaceholder') || 'Variety',
    options: productOptions,
    value: filter.product_id || undefined,
    onChange: handleProductSelectChange,
    filterOption,
    ...drawerFilterSelectProps,
  }

  const seedCropFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.filters.seedCropLabel'),
    placeholder: t('list.filters.seedCropPlaceholder') || 'Seed Crop',
    options: cropOptions,
    value: filter.seed_crop_id || undefined,
    onChange: handleCropSelectChange,
    filterOption,
    ...drawerFilterSelectProps,
  }

  const seedClassFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.filters.seedClassLabel'),
    placeholder: t('list.filters.seedClassPlaceholder') || 'Seed Class',
    options: classOptions,
    value: filter.seed_class_id || undefined,
    onChange: handleClassSelectChange,
    filterOption,
    ...drawerFilterSelectProps,
  }

  const growerFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.filters.growerLabel'),
    placeholder: t('list.filters.growerPlaceholder'),
    options: growerOptions,
    value: filter.company_id || undefined,
    onChange: handleGrowerChange,
    filterOption,
    ...drawerFilterSelectProps,
  }

  useHelmet({ title: t('seedTrackerMetaTitle') })

  return (
    <>
      <Flex style={seedTrackerHeaderStyles} justify={'space-between'}>
        <Header.Title title={t('list.heading')} />
        <AntDHeader />
      </Flex>
      <div style={seedTrackerContentStyles}>
        <div style={tabContentWrapperStyles}>
          <Flex vertical gap="middle" style={filterActionBarStyles}>
            <Flex justify="space-between" align="center" flex={1}>
              <Search
                placeholder={t('list.searchPlaceholder')}
                onSearch={onSearch}
                value={filter.search}
                allowClear
                onChange={(e) => {
                  if (!e.target.value) {
                    handleSearchChange(undefined)
                  }
                }}
                style={searchMaxWidthStyle}
              />
              <Button onClick={downloadCSV} loading={progressCSV === Progress.WORK}>
                {t('labels:downloadCsv')}
              </Button>
            </Flex>
            <Flex vertical gap="middle" flex={1}>
              <Flex gap="middle" align="center" wrap="wrap">
                <GenericSelect {...brandFilterProps} />
                <GenericSelect {...varietyFilterProps} />
                <GenericSelect {...seedCropFilterProps} />
                <GenericSelect {...seedClassFilterProps} />
              </Flex>
              <Flex gap="middle" align="center" wrap="wrap">
                <div style={growerFilterWidthStyles}>
                  <GenericSelect {...growerFilterProps} />
                </div>
                {filter.company_id ? (
                  <div style={growerFilterActiveTextStyles}>{t('list.filters.growerActiveText')}</div>
                ) : null}
              </Flex>
              <Button type="default" onClick={() => setFiltersDrawerOpen(true)} style={{ display: 'none' }}>
                {t('detail.seedInventory.filtersButton')}
              </Button>
            </Flex>
          </Flex>
          <div style={scrollAreaStyle}>
            <SeedTrackerList />
          </div>
          <Drawer
            title={t('detail.seedInventory.filtersDrawerTitle')}
            placement="right"
            open={filtersDrawerOpen}
            onClose={() => setFiltersDrawerOpen(false)}
            width={378}
            destroyOnClose={false}
          >
            <Flex vertical gap="large" style={drawerFilterStackStyle}>
              <div style={drawerFilterStackStyle}>
                <GenericSelect {...brandFilterProps} />
              </div>
              <div style={drawerFilterStackStyle}>
                <GenericSelect {...varietyFilterProps} />
              </div>
              <div style={drawerFilterStackStyle}>
                <GenericSelect {...seedCropFilterProps} />
              </div>
              <div style={drawerFilterStackStyle}>
                <GenericSelect {...seedClassFilterProps} />
              </div>
              <div style={drawerFilterStackStyle}>
                <Flex vertical gap={4}>
                  <GenericSelect {...growerFilterProps} />
                  {filter.company_id ? (
                    <div style={growerFilterActiveTextStyles}>{t('list.filters.growerActiveText')}</div>
                  ) : null}
                </Flex>
              </div>
            </Flex>
          </Drawer>
        </div>
      </div>
    </>
  )
}

export default SeedTrackerTable
